/**
 * Dictation. A clip plays, the learner types what they heard, and the correct
 * text is revealed with a word-level diff.
 *
 * Replay is unlimited and 0.75x is always available. Neither is penalised —
 * the goal is perception, not performance (docs/01).
 */

import { api, ApiError } from "../api.ts";
import { play, stop } from "../audio.ts";
import type { AttemptRecord, DictationPayload, DictationResponse } from "../types.ts";
import { el, gloss, primaryButton, ruleTil } from "../ui.ts";
import { uuid, type BlockContext, type RenderedBlock } from "./types.ts";

const WAVE_MARKS = 11;

export function renderDictation(context: BlockContext): RenderedBlock {
	const payload = context.block.payload as unknown as DictationPayload;
	const shownAt = Date.now();
	let slow = false;
	let playing = false;
	let submitted = false;

	const body: Node[] = [];

	const wave = el("div", { class: "wave", "aria-hidden": "true" });
	const renderWave = (fraction: number): void => {
		const filled = Math.round(fraction * WAVE_MARKS);
		wave.replaceChildren();
		for (let i = 0; i < WAVE_MARKS; i += 1) {
			wave.append(el(i < filled ? "b" : "i", { text: "~" }));
		}
	};
	renderWave(0);

	const playButton = el(
		"button",
		{
			class: "play",
			type: "button",
			"aria-label": "Play the clip",
		},
		iconPlay(),
	);

	const startPlayback = (): void => {
		if (payload.audio_url === "") return;
		if (playing) {
			stop();
			playing = false;
			playButton.classList.remove("play--playing");
			return;
		}
		playing = true;
		playButton.classList.add("play--playing");
		play(payload.audio_url, {
			rate: slow ? 0.75 : 1,
			onProgress: renderWave,
			onEnd: () => {
				playing = false;
				playButton.classList.remove("play--playing");
			},
		});
	};
	playButton.addEventListener("click", startPlayback);

	const card = el("div", { class: "card player" }, playButton, wave);
	body.push(card);

	if (payload.audio_url === "") {
		body.push(
			el("div", {
				class: "notice",
				style: "margin-top:10px",
				text: "This clip could not be synthesized. The sentence is written out below instead.",
			}),
		);
		body.push(el("p", { class: "pt-lg", lang: "pt-BR", style: "margin-top:10px", text: payload.sentence_pt }));
	}

	const replay = el("button", { class: "mini", type: "button", text: "Replay", onclick: startPlayback });
	const slower = el("button", {
		class: "mini",
		type: "button",
		"aria-pressed": "false",
		text: "0.75× speed",
	});
	slower.addEventListener("click", () => {
		slow = !slow;
		slower.setAttribute("aria-pressed", slow ? "true" : "false");
		if (playing) {
			stop();
			playing = false;
			playButton.classList.remove("play--playing");
			startPlayback();
		}
	});
	body.push(el("div", { class: "mini-row" }, replay, slower));

	body.push(ruleTil());

	const input = el("textarea", {
		lang: "pt-BR",
		rows: "2",
		placeholder: "Escreva o que você ouviu…",
		"aria-label": "Type what you heard",
	});
	body.push(el("div", { class: "composer" }, input));

	const result = el("div", { class: "stack--tight", "aria-live": "polite" });
	body.push(result);

	const attempts: AttemptRecord[] = [];
	const attemptId = uuid();

	const check = async (): Promise<void> => {
		if (submitted) return;
		const text = input.value.trim();
		if (text === "") return;
		submitted = true;
		input.disabled = true;
		stop();

		if (context.isTest) {
			// Deferred to submission: grading happens once, at the end.
			context.finish({
				attempts: [],
				deferred: { id: attemptId, block_id: context.block.id, text },
			});
			return;
		}

		context.setFooter(primaryButton("Checking…", () => {}, { disabled: true }));

		try {
			const diff = await api.dictation(context.session.id, { block_id: context.block.id, text });
			renderDiff(diff, text);
			attempts.push({
				id: attemptId,
				session_id: context.session.id,
				block_id: context.block.id,
				item_id: payload.item_id === 0 ? null : payload.item_id,
				modality: "listening",
				correct: diff.accuracy >= 0.8,
				confidence: null,
				response_text: text,
				elapsed_ms: Date.now() - shownAt,
				created_at: Date.now(),
			});
		} catch (error) {
			const message = error instanceof ApiError ? error.message : "Grading needs a connection.";
			result.replaceChildren(el("div", { class: "notice", text: message }));
			// Offline: record what they typed and move on. The reference is
			// already on the device, so the learner still sees the answer.
			attempts.push({
				id: attemptId,
				session_id: context.session.id,
				block_id: context.block.id,
				item_id: payload.item_id === 0 ? null : payload.item_id,
				modality: "listening",
				correct: normalize(text) === normalize(payload.sentence_pt),
				confidence: null,
				response_text: text,
				elapsed_ms: Date.now() - shownAt,
				created_at: Date.now(),
			});
			result.append(el("div", { class: "meta", style: "margin-top:18px", text: "Actual" }));
			result.append(el("p", { class: "pt-lg", lang: "pt-BR", style: "margin-top:7px", text: payload.sentence_pt }));
		} finally {
			context.setFooter(primaryButton("Continue", () => context.finish({ attempts })));
		}
	};

	function renderDiff(diff: DictationResponse, typed: string): void {
		result.replaceChildren();
		result.append(el("div", { class: "meta", text: "You heard" }));
		result.append(
			el("p", { class: "pt", lang: "pt-BR", style: "margin-top:7px;color:var(--ink-muted)", text: typed }),
		);

		result.append(el("div", { class: "meta", style: "margin-top:18px", text: "Actual" }));

		// The shape of the error should be legible before any of the text is
		// read (docs/05).
		const reference = el("p", { class: "pt-lg", lang: "pt-BR", style: "margin-top:7px" });
		const substitutions: string[] = [];
		for (const token of diff.tokens) {
			if (token.status === "match") {
				reference.append(el("span", { class: "diff__ok", text: token.expected }), " ");
			} else if (token.status === "omission") {
				reference.append(el("span", { class: "diff__miss", text: token.expected }), " ");
			} else if (token.status === "substitution") {
				reference.append(el("span", { class: "diff__miss", text: token.expected }), " ");
				if (token.heard !== "") substitutions.push(token.heard);
			}
		}
		result.append(reference);

		if (substitutions.length > 0) {
			const struck = el("p", { class: "en", style: "margin-top:5px;font-size:12.5px" });
			for (const heard of substitutions) {
				struck.append(el("span", { class: "diff__sub", lang: "pt-BR", text: heard }), "  ");
			}
			result.append(struck);
		}

		result.append(
			el("div", { class: "stat", style: "margin-top:16px" }, el("div", { class: "stat__label", text: "Accuracy" }), el("div", { class: "stat__value", text: `${Math.round(diff.accuracy * 100)}%` })),
		);

		if (diff.summary_en.trim() !== "") {
			result.append(el("div", { class: "notice", style: "margin-top:16px", lang: "en", text: diff.summary_en }));
		}
		const glossLine = gloss(payload.gloss_en, context.englishRatio);
		if (glossLine !== null) result.append(glossLine);
	}

	const footer = primaryButton("Check answer", () => {
		void check();
	});

	return { body, footer };
}

function normalize(text: string): string {
	return text.trim().toLowerCase().replace(/[.,!?;:]/g, "").replace(/\s+/g, " ");
}

function iconPlay(): SVGElement {
	const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
	svg.setAttribute("viewBox", "0 0 24 24");
	svg.setAttribute("aria-hidden", "true");
	const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
	path.setAttribute("d", "M8 5v14l11-7z");
	svg.append(path);
	return svg;
}
